// Ctrl+O fuzzy quick switcher over every markdown note in the vault.
import { basename, h, stripMarkdownExt } from './dom.js';
import { openModal } from './modals.js';
import type { App } from './app.js';

/** Subsequence score: lower is better, -1 means "no match". */
function fuzzyScore(query: string, text: string): number {
  if (!query) return 0;
  const haystack = text.toLowerCase();
  let index = 0;
  let score = 0;
  let streak = 0;
  for (const char of query.toLowerCase()) {
    const found = haystack.indexOf(char, index);
    if (found === -1) return -1;
    streak = found === index ? streak + 1 : 0;
    score += found - index + (streak > 1 ? 0 : 6);
    index = found + 1;
  }
  return score + haystack.length * 0.01;
}

export class QuickSwitcher {
  private readonly app: App;

  constructor(app: App) {
    this.app = app;
  }

  open(): void {
    const modal = openModal({ title: 'Quick switcher', kind: 'quick' });
    const input = h('input', {
      class: 'text-input quick-input',
      type: 'text',
      placeholder: 'Type a note name…',
      attrs: { 'aria-label': 'Quick switcher' },
    });
    const list = h('div', { class: 'quick-list' });
    modal.body.append(input, list);
    let matches: { path: string; score: number }[] = [];
    let cursor = 0;
    let creating = false;

    const render = (): void => {
      list.replaceChildren();
      const rows: HTMLElement[] = [];
      matches.forEach((match, index) => {
        const row = h('button', {
          class: `quick-row${index === cursor && !creating ? ' active' : ''}`,
          type: 'button',
        }, [
          h('span', { class: 'quick-title', text: stripMarkdownExt(basename(match.path)) }),
          h('span', { class: 'quick-path', text: match.path }),
        ]);
        row.addEventListener('click', () => {
          modal.close();
          void this.app.open(match.path);
        });
        rows.push(row);
      });
      const typed = input.value.trim();
      if (typed) {
        const create = h('button', {
          class: `quick-row quick-create${creating ? ' active' : ''}`,
          type: 'button',
          text: `Create note “${typed}”`,
        });
        create.addEventListener('click', () => {
          modal.close();
          void this.app.createNoteWithName(typed);
        });
        rows.push(create);
      }
      for (const row of rows) list.append(row);
      if (rows.length === 0) list.append(h('p', { class: 'empty-note', text: 'No matching notes.' }));
    };

    const filter = (): void => {
      const query = input.value.trim();
      const notes = this.app.filePaths.filter((path) => /\.(md|markdown)$/i.test(path));
      if (!query) {
        matches = notes.slice(0, 60).map((path) => ({ path, score: 0 }));
      } else {
        matches = notes
          .map((path) => ({ path, score: fuzzyScore(query, stripMarkdownExt(path)) }))
          .filter((match) => match.score >= 0)
          .sort((a, b) => a.score - b.score || a.path.localeCompare(b.path))
          .slice(0, 60);
      }
      cursor = 0;
      creating = false;
      render();
    };

    input.addEventListener('input', filter);
    input.addEventListener('keydown', (event) => {
      const total = matches.length + (input.value.trim() ? 1 : 0);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        cursor = total === 0 ? 0 : (cursor + 1) % total;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        cursor = total === 0 ? 0 : (cursor - 1 + total) % total;
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const typed = input.value.trim();
        if (cursor === matches.length && typed) {
          modal.close();
          void this.app.createNoteWithName(typed);
          return;
        }
        const match = matches[cursor];
        if (match) {
          modal.close();
          void this.app.open(match.path);
        }
      }
      creating = cursor === matches.length && input.value.trim().length > 0;
      render();
    });
    filter();
    input.focus();
  }
}
